/**
 * Cart projection helpers & optimistic state management.
 *
 * The live cart projection mirrors choosechow_cart_items rows, which keep
 * their full lifecycle: 'active' → 'ordered' (checked out) or 'removed'.
 * Every cart read in the UI scopes to active lines.
 *
 * All cart writes apply an optimistic local update into the cartStore immediately,
 * providing 0ms responsive UI feedback, while dispatching the backend command.
 * Server deltas seamlessly reconcile state, and errors roll back cleanly.
 */
import type { TenantProjectionStore } from '@ovasabi/frontend-kit'
import { prototypeStoreRegistry } from '../../generated/prototypeRuntime'
import { commandPayload, commands, isLiveRuntime } from '../../lib/runtime'
import type { UiDish } from './projections'
import { beginCartWrite } from './cartWrites'

export const isActiveCartRecord = (record: Record<string, unknown>): boolean =>
  String(record.status ?? 'active') === 'active'

export const activeCartRecords = (
  records: readonly Record<string, unknown>[],
): readonly Record<string, unknown>[] => {
  const active = records.filter(isActiveCartRecord)
  const map = new Map<string, Record<string, unknown>>()
  for (const r of active) {
    const key = String(r.item_ref || r.id)
    const existing = map.get(key)
    if (!existing) {
      map.set(key, r)
    } else {
      const isOpt = String(r.id).startsWith('opt-')
      const existingIsOpt = String(existing.id).startsWith('opt-')
      if (existingIsOpt && !isOpt) {
        map.set(key, { ...r, quantity: Math.max(Number(r.quantity ?? 1), Number(existing.quantity ?? 1)) })
      } else {
        map.set(key, { ...existing, quantity: Math.max(Number(r.quantity ?? 1), Number(existing.quantity ?? 1)) })
      }
    }
  }
  return Array.from(map.values())
}

/** Scope before deduplication so another customer's rows cannot affect quantities. */
export const customerCartRecords = (
  records: readonly Record<string, unknown>[], profileId: string, live: boolean,
): readonly Record<string, unknown>[] => activeCartRecords(
  live ? records.filter(row => Boolean(profileId) && String(row.customer_profile_id ?? '') === profileId) : records,
)

const getCartStore = (): TenantProjectionStore<Record<string, unknown>> | undefined => {
  const raw = prototypeStoreRegistry.get('marketplace.carts')
  if (!raw) return undefined
  return raw as unknown as TenantProjectionStore<Record<string, unknown>>
}

/**
 * Add a dish to the cart — the write path shared across dishes list, detail,
 * and storefront pages.
 *
 * Optimistically updates the projection store immediately for instant UI
 * feedback, then dispatches the backend command in live mode.
 */
export const addDishToCart = async (dish: UiDish, quantity: number, customerProfileId: string): Promise<void> => {
  const cartStore = getCartStore()
  if (!cartStore) return

  // An "add" must add at least one. The command treats quantity 0 as REMOVE, so
  // a glitched delta (0, NaN, negative — e.g. a stale qty-state value) reaching
  // the wire would silently delete the line and echo its old quantity back
  // instead of adding. Clamp to a positive whole number here, the one chokepoint.
  const delta = Math.max(1, Math.floor(Number(quantity)) || 1)
  const snapshot = cartStore.getSnapshot().records
  const existing = activeCartRecords(snapshot).find(
    (item) => String(item.item_ref) === dish.id,
  )
  // Guard the existing quantity too: a non-numeric/absent value must read as 0,
  // never NaN (NaN + delta serialises to null → the backend parses it as 0 →
  // REMOVE). This is the other half of the "add sent 0, came back 14" bug.
  const currentQty = existing ? Math.max(0, Math.floor(Number(existing.quantity)) || 0) : 0
  const newQty = currentQty + delta
  const optimisticId = existing ? String(existing.id) : `opt-cart-${dish.id}`
  const finishWrite = isLiveRuntime() ? beginCartWrite() : () => {}

  // 1. Optimistic write directly into local store for 0-latency UI
  if (existing) {
    cartStore.upsert({ ...existing, quantity: newQty, status: 'active' })
  } else {
    cartStore.upsert({
      id: optimisticId,
      item_name: dish.name,
      item_ref: dish.id,
      quantity: newQty,
      unit_amount_minor: Math.round(dish.price * 100),
      currency: 'NGN',
      customer_profile_id: customerProfileId,
      merchant_profile_id: dish.chefId,
      organization_id: 'org_default',
      special_instructions: '',
      created_at: new Date().toISOString(),
      status: 'active',
    })
  }

  if (!isLiveRuntime()) return

  // 2. Live command dispatch with UUID reconciliation
  try {
    const response = await commands.marketplace.addCartItem({
      customer_profile_id: customerProfileId,
      merchant_profile_id: dish.chefId,
      item_ref: dish.id,
      item_name: dish.name,
      quantity: newQty,
      unit_amount_minor: Math.round(dish.price * 100),
      currency: 'NGN',
      special_instructions: '',
    })
    const res = commandPayload(response)

    if (res && res.id) {
      if (String(res.id) !== optimisticId) {
        cartStore.remove?.(optimisticId)
      }
      // Respect the server's status. Forcing 'active' here re-animated a row the
      // backend had just REMOVED (e.g. a stray quantity-0 write), resurrecting a
      // deleted line at its old quantity. A removed row is dropped, not upserted.
      const serverStatus = String(res.status ?? 'active')
      if (serverStatus === 'active') {
        cartStore.upsert({ ...res, status: 'active' })
      } else {
        cartStore.remove?.(String(res.id))
      }
    }
  } catch (err) {
    console.error('Failed to add cart item:', err)
    // Rollback on server error
    const rollbackStore = getCartStore()
    if (rollbackStore) {
      if (existing) {
        rollbackStore.upsert(existing)
      } else {
        rollbackStore.remove?.(optimisticId)
      }
    }
    // Surface the failure instead of a silent rollback. A merchant_not_found
    // (orphan dish whose kitchen no longer resolves) gets a specific line; every
    // other failure gets a generic retry line. A listener in the app shell shows
    // it as a toast. Rolling back without a word is what made adds look flaky.
    emitCartError(cartErrorMessage(err, dish.name))
  } finally {
    finishWrite()
  }
}

/** Reduce a cart-add failure to one actionable line for the customer. */
const cartErrorMessage = (err: unknown, dishName: string): string => {
  const code = (err as { code?: string })?.code
  const message = err instanceof Error ? err.message.trim() : ''
  if (code === 'merchant_not_found' || /kitchen is unavailable/i.test(message)) {
    return `${dishName} is unavailable — its kitchen is no longer open on ChooseChow.`
  }
  return `Couldn't add ${dishName} to your cart. Please try again.`
}

/**
 * Broadcast a cart error on the window so a single toast in the app shell can
 * show it, without threading callbacks through every Add button. Mirrors the
 * existing `chow:auth:unauthorized` global-event idiom.
 */
export const CART_ERROR_EVENT = 'chow:cart:error'
export const emitCartError = (message: string, title = "Couldn't add to cart"): void => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(CART_ERROR_EVENT, { detail: { message, title } }))
}

/**
 * Set the absolute quantity of one cart line — the write behind the −/+
 * stepper, and behind removing a line (quantity 0).
 *
 * `marketplace:add_cart_item` carries SET semantics: the server writes the
 * absolute quantity it is given and treats 0 as "deactivate this line". So one
 * command covers both directions, and a retry can never double-count.
 *
 * The local store is written FIRST, in BOTH runtimes. The live path used to
 * dispatch and then wait for the projection to bring the change back, which is
 * why a removed line stayed on screen: the mirror converges a non-active cart
 * row by dropping it, so there was no row left to re-deliver and nothing ever
 * took the line away. A failed command rolls the line back to what it was and
 * says so — a silent rollback is what made removal look broken.
 */
/** The `add_cart_item` payload for one existing line at an absolute quantity. */
const cartLineCommand = (
  item: Record<string, unknown>,
  quantity: number,
  customerProfileId: string,
): Record<string, unknown> => ({
  customer_profile_id: String(item.customer_profile_id ?? customerProfileId),
  merchant_profile_id: String(item.merchant_profile_id ?? ''),
  item_ref: String(item.item_ref ?? ''),
  item_name: String(item.item_name ?? ''),
  quantity,
  unit_amount_minor: Number(item.unit_amount_minor ?? 0),
  currency: String(item.currency ?? 'NGN'),
  special_instructions: String(item.special_instructions ?? ''),
})

/**
 * Send one absolute cart-line quantity and reconcile the local mirror against
 * the row the server actually wrote. Shared by the stepper and by checkout's
 * rollback so a line can never be reconciled two different ways.
 *
 * A removal answers with the deactivated row (or nothing at all), so only an
 * `active` row is worth keeping locally; anything else leaves the line gone.
 */
const commitCartLine = async (
  item: Record<string, unknown>,
  id: string,
  quantity: number,
  _previousQuantity: number,
  customerProfileId: string,
): Promise<void> => {
  const cartStore = getCartStore()
  if (!cartStore) return
  try {
    const response = await commands.marketplace.addCartItem(
      cartLineCommand(item, quantity, customerProfileId),
    )
    const res = commandPayload(response)
    const serverId = String(res?.id ?? '')
    if (quantity === 0) {
      cartStore.remove?.(id)
      if (serverId) cartStore.remove?.(serverId)
      return
    }
    if (!serverId) return
    if (String(res?.status ?? 'active') === 'active') {
      if (serverId !== id) cartStore.remove?.(id)
      cartStore.upsert({ ...res, status: 'active' })
    } else {
      cartStore.remove?.(serverId)
      cartStore.remove?.(id)
    }
  } catch (err) {
    console.error('Failed to update cart item:', err)
    cartStore.upsert(item)
    const name = String(item.item_name ?? 'that item')
    emitCartError(
      quantity === 0
        ? `Couldn't remove ${name} from your cart. Please try again.`
        : `Couldn't change how many ${name} you're ordering. Please try again.`,
      quantity === 0 ? "Couldn't remove that item" : "Couldn't update your cart",
    )
  }
}

export const setCartLineQuantity = async (
  item: Record<string, unknown>,
  nextQuantity: number,
  customerProfileId: string,
): Promise<void> => {
  const cartStore = getCartStore()
  if (!cartStore) return
  const id = String(item.id ?? '')
  if (!id) return

  const quantity = Math.max(0, Math.floor(Number(nextQuantity)) || 0)
  const previousQuantity = Math.max(0, Math.floor(Number(item.quantity)) || 0)
  // A no-op step is not worth a command — but a removal always is: a line whose
  // stored quantity is missing or unreadable reads as 0 here, and that line
  // still has to be removable.
  if (quantity > 0 && quantity === previousQuantity) return
  const finishWrite = isLiveRuntime() ? beginCartWrite() : () => {}

  if (quantity === 0) {
    cartStore.remove?.(id)
  } else {
    cartStore.upsert({ ...item, quantity, status: 'active' })
  }

  if (!isLiveRuntime()) return

  try {
    await commitCartLine(item, id, quantity, previousQuantity, customerProfileId)
  } finally {
    finishWrite()
  }
}

/**
 * Hand the cart back after a checkout that never reached payment.
 *
 * Checkout flips lines to `ordered` before the customer is charged, so a
 * declined or abandoned payment would otherwise leave the cart looking full
 * while the server holds no active line to sell. The active-line unique index
 * covers only `status = 'active'` rows, so re-sending the same item writes a
 * fresh active row instead of colliding with the spent one.
 *
 * Skips the no-op guard in `setCartLineQuantity` on purpose: the quantity is
 * unchanged here, the row's state is not.
 */
export const restoreCartLines = async (
  lines: readonly Record<string, unknown>[],
  customerProfileId: string,
): Promise<void> => {
  const cartStore = getCartStore()
  if (!cartStore) return
  for (const line of lines) {
    const quantity = Math.max(1, Math.floor(Number(line.quantity)) || 1)
    cartStore.upsert({ ...line, quantity, status: 'active' })
    if (!isLiveRuntime()) continue
    await commitCartLine(line, String(line.id ?? ''), quantity, quantity, customerProfileId)
  }
}

/**
 * Drop every active line of one customer's cart, one command per line (there
 * is no bulk clear on the wire). Failures surface per line through
 * `setCartLineQuantity`, so a partial clear is visible rather than silent.
 */
export const clearCartLines = async (
  lines: readonly Record<string, unknown>[],
  customerProfileId: string,
): Promise<void> => {
  await Promise.all(lines.map((line) => setCartLineQuantity(line, 0, customerProfileId)))
}

/**
 * Drop cart lines the backend has already moved on from — the lines that just
 * became an order. Local-only: checkout has already flipped them to `ordered`
 * server-side, so there is nothing left to command, only a local copy to shed
 * while the mirror catches up.
 */
export const dropCartLines = (cartItemIds: readonly string[]): void => {
  const cartStore = getCartStore()
  if (!cartStore) return
  for (const id of cartItemIds) cartStore.remove?.(id)
}
